import ExpandingChart from "features/dashboard/MobileDashboard/ExpandingChart.tsx";
import React, { useContext, useEffect, useState } from "react";
import {
  FiltersRequest,
  LanguageNameSpaces,
  TimeRange,
  TradeGraphType,
} from "@shared/types";
import LineChart from "common/Charts/LineChart/LineChart.tsx";
import BarChart from "common/Charts/BarChart.tsx";
import styled from "styled-components";
import useMedia from "utils/hooks/useMedia.ts";
import Card from "common/Boxes/Card.tsx";
import Tabs from "common/Tabs/Tabs.tsx";
import ChartTimeRangeButtons from "Global/ChartTimeRangeButtons.jsx";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import useTradesGraphData from "features/trades/TradesCharts/useTradesGraphData.ts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TradesExpandingChartProps {
  expanded?: boolean;
  filters?: FiltersRequest;
  precision?: number;
  setChartFetching?: (chartFetching: boolean) => void;
}

const TradesGraphTabs = ({
  expanded,
  filters,
  precision,
  setChartFetching,
}: TradesExpandingChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const [tab, setTab] = useState<TradeGraphType>(
    TradeGraphType.ACCUMULATING_PROFIT
  );

  const {
    displayCurrency,
    timeRange,
    setTimeRange,
    isFetching: baseIsFetching,
    isSuccess,
    accumulatedProfitGraphData,
    profitByDayGraphData,
    refetch,
  } = useTradesGraphData({ filters, precision });
  const { getTradesQuery } = useContext(TradesPageContext) || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isFetching = baseIsFetching && !isBottomNavigator;

  useEffect(() => {
    if (getTradesQuery?.isFetching) {
      try {
        refetch();
      } catch {}
    }
  }, [getTradesQuery?.isFetching]);

  useEffect(() => {
    setChartFetching?.(isFetching);
  }, [isFetching]);

  const chartProps = {
    loading: isFetching,
    displayCurrency,
    precision,
    isSuccess,
    height: 230,
  };

  const accumulatedProfitChart = (
    <ChartWrapper>
      <LineChart data={accumulatedProfitGraphData} {...chartProps} />
    </ChartWrapper>
  );

  const profitByDayChart = (
    <ChartWrapper>
      <BarChart
        data={profitByDayGraphData}
        usePositiveNegativeColorScheme={true}
        {...chartProps}
      />
    </ChartWrapper>
  );

  const tabsItems = [
    {
      key: TradeGraphType.ACCUMULATING_PROFIT.toString(),
      label: t("charts.accumulated_profit"),
      children: accumulatedProfitChart,
      data: accumulatedProfitGraphData,
    },
    {
      key: TradeGraphType.PROFIT_BY_DAY.toString(),
      label: t("charts.profit_by_day"),
      children: profitByDayChart,
      data: profitByDayGraphData,
      isBarChart: true,
    },
  ];

  if (isMobile) {
    return (
      <ExpandingChart
        tabsItems={tabsItems}
        selectedTimeRange={timeRange}
        setSelectedTimeRange={setTimeRange}
        precision={precision}
        loading={isFetching}
        isSuccess={isSuccess}
        expanded={expanded}
        usePositiveNegativeColorScheme={true}
      />
    );
  }

  return (
    <ChartCard>
      <Tabs
        activeKey={tab.toString()}
        onTabClick={(tabKey) => setTab(parseInt(tabKey))}
        size="small"
        $hideBorder={true}
        items={tabsItems}
        tabBarStyle={{ margin: "0 0 8px 0" }}
        style={{ display: "grid" }}
      />
      <div style={{ marginTop: 8 }}>
        <ChartTimeRangeButtons
          selectedTimeRange={timeRange}
          handleSelectTimeRange={(timeRange: TimeRange) => {
            setTimeRange(timeRange);
          }}
          buttonSize="medium"
        />
      </div>
    </ChartCard>
  );
};

export default TradesGraphTabs;

const ChartCard = styled(Card)`
  overflow: hidden;
  padding: 8px 16px 16px;
  height: 100%;
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const ChartWrapper = styled.div`
  margin-top: 8px;
  height: 230px;
  width: 100%;
`;
